/**
 * What the Parts screen shows, apart from anything that draws it.
 *
 * Every function here is pure and exported because both answers this screen
 * gives are ones a mistake renders *plausibly*:
 *
 *   - the definitions listing is a FILTER, and the tempting version of it -
 *     "drop everything that is not a definition" - loses the root instance of
 *     the design, which is a usage owned by nothing. The screen would look
 *     tidier and the top of the tree would be gone;
 *   - the tree is rebuilt from flat rows carrying a depth, and an off-by-one
 *     there reparents half the design and still draws a perfectly believable
 *     picture.
 *
 * `is_abstract` is read defensively rather than as a field. It is being added to
 * the parts payload while this screen is written, so a row may not carry it yet;
 * a row that says nothing is treated as concrete rather than as an error, and
 * `modifiers` is honoured if that arrives first instead. Neither is guessed at
 * from the name.
 *
 * `resolveToDefinition` is the third answer of this kind, and the one Patrick
 * reported as "hit-or-miss about grabbing the usage or the definition". It is
 * pure and tested directly for the same reason as the other two: landing on the
 * wrong one of the pair renders a complete, plausible page - the usage's, with no
 * attributes, no masses and no decomposition on it - and nothing about that page
 * says it is the wrong place to be.
 */

import { attributeOf, groupByAttribute, groupLabelOf, isCostly, leafOf } from '../../schema'
import type {
  DesignRow,
  FieldGroup,
  FieldSchema,
  KindSchema,
  PartDetail,
  PartRow,
} from '../../schema'

/** How this person may act on the model, as `useCanWrite()` answers it. */
export interface WritePermission {
  allowed: boolean
  reason: string
}

/**
 * One row of the definitions listing, with why it is on it.
 *
 * `isInstance` is not "is a usage": every usage the listing keeps is one, but
 * the ones it drops are usages too. It means "this is here because nothing
 * contains it", which is exactly the fact that has to be visible - otherwise a
 * definition and the root instance of the design read as the same thing.
 */
export interface ListedPart {
  row: PartRow
  isInstance: boolean
  isGeneric: boolean
}

/** The definitions listing, and what it left out. */
export interface DefinitionsListing {
  listed: ListedPart[]
  /**
   * How many usages were dropped because another part already shows them.
   *
   * Reported rather than silent: "15 parts, 12 shown" is a question somebody
   * will have, and the answer is that the other three are on their
   * definition's pane rather than missing.
   */
  contained: number
}

/**
 * Whether a part is a generic kind - abstract, so specialisable but never used.
 *
 * @param row a part row or detail, which may not carry the flag yet
 * @returns true only when the payload says so; never inferred from the name
 */
export function isGenericKind(row: Record<string, unknown>): boolean {
  // `is_abstract` is on PartRow and PartDetail now, so a caller passing either
  // is typed. The signature stays permissive on purpose: this is also handed
  // rows from /api/design and from `contains[]`, neither of which carries the
  // flag, and returning false for them is the right answer rather than a type
  // error at every call site.
  if (typeof row.is_abstract === 'boolean') return row.is_abstract
  // `modifiers` is the column `is_abstract` is derived from, honoured so the
  // label is right whichever of the two reaches the browser first.
  if (typeof row.modifiers === 'string') {
    return row.modifiers.split(/\s+/).includes('abstract')
  }
  return false
}

/**
 * The definitions view: every definition, plus every usage nothing contains.
 *
 * The second half is the part worth stating. A contained usage is dropped
 * because it is shown on its definition's decomposition pane, so nothing is
 * lost by leaving it out here - but an UNCONTAINED usage appears nowhere else,
 * and the design's root instance is one of them. It is kept and marked.
 *
 * Order is the listing's own, which is declaration order, so the screen agrees
 * with the file.
 *
 * @param rows every part, as `GET /api/parts` answers it
 */
export function definitionsListing(rows: PartRow[]): DefinitionsListing {
  const listed: ListedPart[] = []
  let contained = 0

  for (const row of rows) {
    if (!row.is_definition && row.is_contained) {
      contained += 1
      continue
    }
    listed.push({
      row,
      isInstance: !row.is_definition,
      isGeneric: isGenericKind(row as unknown as Record<string, unknown>),
    })
  }

  return { listed, contained }
}

/**
 * Multiplicity is not a column of a table of DEFINITIONS.
 *
 * Patrick's fifth observation, and it is a modelling point rather than a layout
 * one: multiplicity characterises a USAGE inside its parent - four of these, one
 * of those - so a definition has none to state, and a column of blanks invited
 * somebody to fill one in where it would mean nothing. It stays where it is a
 * fact: on the `contains[]` rows of a definition's pane, and on the tree.
 */
export const USAGE_ONLY_FIELDS = new Set(['multiplicity'])

/**
 * The definitions table's columns, taken from the registry rather than listed.
 *
 * A field added to the part kind on the back end becomes a column with nothing
 * here being touched, which is the whole point of `/api/schema`. Four are left
 * out: references and lists, because the LISTING endpoint omits them to save a
 * query per row and an always-blank column is worse than none; `identifier`,
 * which is the ID column; `name`, which is rendered with the qualified name
 * beneath it; and `multiplicity`, which belongs to a usage rather than to a
 * definition - see `USAGE_ONLY_FIELDS`.
 *
 * AND THE ATTRIBUTES THAT ARE NOT COMMON TO EVERY PART. The registry describes
 * all thirty-odd the profile declares, and turning each into a column made
 * BALROG's listing 47 columns wide - of which three said anything about more
 * than one row, because an inertia tensor belongs to the vehicle and a chamber
 * pressure to a thruster. A column that is blank on every row but one is not a
 * column; it is the detail pane's job, and the vehicle's own figures have a
 * page of their own.
 *
 * `common` is the SERVER'S answer, from the model: the attributes declared on a
 * type every part reaches. Filtering to a hard-coded 'EchelonPart' here would be
 * the parallel list the model has to wait on - the thing storing values by
 * feature path removed - and it would be wrong for a program whose parts share a
 * base of their own.
 *
 * Passing no `common` keeps every column, which is what an older server's
 * listing gets: a wide table is a great deal better than an empty one.
 *
 * @param kind the `parts` entry from `GET /api/schema`
 * @param common `common_attributes` from `GET /api/parts`, or undefined
 */
function scalarColumns(
  kind: KindSchema,
  common?: readonly string[] | null,
): FieldSchema[] {
  const shared = common ? new Set(common) : null
  return kind.fields.filter(
    (field) =>
      !isCostly(field) &&
      field.name !== 'identifier' &&
      field.name !== 'name' &&
      !USAGE_ONLY_FIELDS.has(field.name) &&
      // A field binding no feature is a COLUMN on the projection - the role,
      // the type, the flags - and is about every part by construction. Only
      // the model-declared attributes are filtered.
      (!shared || !field.feature || shared.has(attributeOf(field))),
  )
}

/**
 * One column of the definitions table - which may be more than one field.
 *
 * A temperature band is `operationalTemp.minTemp` and `operationalTemp.maxTemp`:
 * two registry fields, two values, and ONE fact. Given a column each they sat
 * apart under headings that both read "Operational temp …", and a reader had to
 * pair them up by eye on every row to recover the range the model actually
 * states. So a column is a group, and a group of one is the ordinary case.
 */
export interface PartColumn {
  /** Unique in one table: the attribute, or the lone field's name. */
  key: string
  /** The heading - the attribute's name, with the leaf part removed. */
  label: string
  fields: FieldSchema[]
  /** True where the fields are the ends of a range and read as `min – max`. */
  isRange: boolean
  /** True where every field is computed rather than stated. */
  derived: boolean
}

/**
 * A pair that reads as a range, rather than as two numbers sharing a prefix.
 *
 * Recognised from the LEAF NAMES the model wrote - `minTemp` and `maxTemp` -
 * because that is the model saying these are the ends of one interval. Exactly
 * two, and one of each: three leaves are a structure rather than a range, and
 * two minima are not a range at all. Anything else keeps a column per field,
 * which is never wrong, only wider.
 */
export function readsAsRange(fields: readonly FieldSchema[]): boolean {
  if (fields.length !== 2) return false
  const leaves = fields.map((field) => leafOf(field).toLowerCase())
  return (
    leaves.filter((leaf) => leaf.startsWith('min')).length === 1 &&
    leaves.filter((leaf) => leaf.startsWith('max')).length === 1
  )
}

/** The lower end first, whatever order the registry lists them in. */
export function orderRange(fields: readonly FieldSchema[]): FieldSchema[] {
  return [...fields].sort((left, right) =>
    leafOf(left).toLowerCase().startsWith('min') ? -1 : leafOf(right).toLowerCase().startsWith('min') ? 1 : 0,
  )
}

/** Kept as a named export: the grouping rule now lives in `schema.ts`. */
export const columnLabel = groupLabelOf

/**
 * One column of the definitions table - which may be more than one field.
 *
 * A temperature band is `operationalTemp.minTemp` and `operationalTemp.maxTemp`:
 * two registry fields, two values, and ONE fact.
 */
export interface PartColumn extends FieldGroup {
  /** True where the fields are the ends of a range and read as `min - max`. */
  isRange: boolean
  /** True where every field is computed rather than stated. */
  derived: boolean
}

export function columnsFor(
  kind: KindSchema,
  common?: readonly string[] | null,
): PartColumn[] {
  const columns = groupByAttribute(scalarColumns(kind, common)).map((group) => ({
    ...group,
    isRange: readsAsRange(group.fields),
    derived: group.fields.every((field) => field.derived),
  }))
  for (const column of columns) {
    if (column.isRange) column.fields = orderRange(column.fields)
  }
  return columns
}

/** One node of the containment tree. */
export interface DesignNode {
  /**
   * Unique inside one tree: the row's position in the flat listing.
   *
   * Not the ID or the qualified name. The same definition's internals appear
   * under every usage of it, so both repeat, and a duplicate itemId makes MUI's
   * tree view fold the two rows into one.
   */
  key: string
  row: DesignRow
  children: DesignNode[]
}

/**
 * Rebuild the containment tree from flat rows carrying a depth.
 *
 * `GET /api/design` already walks the model, resolving each usage to its
 * definition and lending it that definition's children and ports - so this is a
 * walk over depths and not a graph solve. A row deeper than its predecessor by
 * more than one still lands somewhere defensible rather than being dropped: a
 * tree that disagrees with the file by an indent is a nuisance, and one that
 * quietly loses a subsystem is why people stop trusting a screen.
 *
 * @param rows the design rows, in the order the endpoint answers them
 */
export function buildDesignTree(rows: DesignRow[]): DesignNode[] {
  const roots: DesignNode[] = []
  const stack: DesignNode[] = []

  rows.forEach((row, index) => {
    // Drop back to this row's parent level BEFORE reading the parent off the
    // stack, so the stack's height is always the depth about to be filled.
    while (stack.length > row.depth) stack.pop()
    const parent = stack.length ? stack[stack.length - 1] : null
    const node: DesignNode = { key: String(index), row, children: [] }
    if (parent) parent.children.push(node)
    else roots.push(node)
    stack.push(node)
  })

  return roots
}

/** Everything about a design row somebody might type to look for it. */
function matches(row: DesignRow, wanted: string): boolean {
  return [row.id, row.name, row.qualified_name, row.type_name]
    .concat(row.ports.map((port) => `${port.name} ${port.type_name}`))
    .some((value) => String(value ?? '').toLowerCase().includes(wanted))
}

/**
 * Narrow the tree to matches, keeping each match's ancestors AND its subtree.
 *
 * The subtree is kept deliberately: a part whose name matches would otherwise
 * be drawn as an empty container, which reads as "nothing inside it matches"
 * rather than "everything in here is inside the thing you asked for".
 */
export function pruneDesignTree(nodes: DesignNode[], text: string): DesignNode[] {
  const wanted = text.trim().toLowerCase()
  if (!wanted) return nodes

  const keep = (node: DesignNode): DesignNode | null => {
    if (matches(node.row, wanted)) return node
    const children = node.children
      .map(keep)
      .filter((child): child is DesignNode => child !== null)
    return children.length ? { ...node, children } : null
  }

  return nodes.map(keep).filter((node): node is DesignNode => node !== null)
}

/** Every key in a set of nodes, for expanding the whole tree at once. */
export function allDesignKeys(nodes: DesignNode[]): string[] {
  return nodes.flatMap((node) => [node.key, ...allDesignKeys(node.children)])
}

export const NOT_A_DEFINITION =
  'Only a part definition can hold child parts. Open the definition this usage ' +
  'is typed by and add the child there, so every usage of it gains the child.'

/**
 * Why "Add child part" is refused, or '' when it is not.
 *
 * Decided in one place, and permission first: somebody who may not write at all
 * is not helped by being told what the control would do. The refusal is returned
 * rather than the button being hidden, because a missing control is
 * indistinguishable from a broken page.
 *
 * An ABSTRACT owner is deliberately not refused: `abstract` constrains
 * instantiation and not decomposition, every specialization inherits the
 * decomposition, and the API accepts it. Only a *child* may not be abstract, and
 * the child picker never offers one because it is fed from
 * `usable_as_type` - the server's own answer - rather than filtered here.
 *
 * @param permission what `useCanWrite()` answered
 * @param isDefinition whether the selected part is a definition
 * @returns the reason to show, or '' when the control is available
 */
export function addChildRefusal(permission: WritePermission, isDefinition: boolean): string {
  if (!permission.allowed) return permission.reason
  if (!isDefinition) return NOT_A_DEFINITION
  return ''
}

// ---------------------------------------------------------------------------
// Where a click lands: the DEFINITION, always
// ---------------------------------------------------------------------------

export const RESOLVES_TO_DEFINITION =
  'A click lands on the DEFINITION. Attributes, masses and the decomposition all ' +
  'live there; a usage is a placement plus a multiplicity, so it contributes that ' +
  'multiplicity as context here rather than being a second place to end up.'

export const UNRESOLVED_USAGE =
  'This usage is typed by nothing this program declares — most often a shared ' +
  'library definition, which is not this program’s to change. So it keeps its own ' +
  'page: there is nowhere else to send it.'

/** A usage that led to a definition: the placement, and how many of it. */
export interface UsageContext {
  id: string
  name: string
  qualified_name: string
  /** As written, e.g. `[4]`. '' is the language default, which is not one. */
  multiplicity: string
  /** What declares the usage, or '' when nothing does - the design's root. */
  owner: string
  /** What it is typed by, as written - `GNC::Guidance`, package short name and all. */
  typeName: string
}

/** Which part's page to show, and what led there. */
export interface Landing {
  /** The part to fetch and draw: the definition, whenever there is one. */
  id: string
  /** The usage clicked, or null when a definition was clicked directly. */
  usage: UsageContext | null
  /**
   * True when a usage kept its own page because its type resolves to nothing.
   *
   * Reported rather than folded into `usage === null`: the two look the same on
   * screen only if the page pretends the usage is a definition, which is how the
   * old behaviour managed to show a part with no attributes and no children and
   * no indication that anything was missing.
   */
  unresolved: boolean
}

/**
 * Resolve whatever was clicked to the DEFINITION it belongs to.
 *
 * Patrick's third observation - "hit-or-miss about grabbing the usage or the
 * definition", worst for top-level subsystems, where `part gnc : GNC::'Guidance
 * Navigation and Control'` and the GNC definition were two different places to
 * end up. The rule is that everything resolves to the definition, and the usage
 * contributes its multiplicity as context.
 *
 * The resolution itself is the SERVER'S: `definition` on the detail is the same
 * answer `contains[].definition` gives, resolved where the package short names in
 * `type_name` can actually be resolved. Nothing here matches a string, which is
 * the mistake this function exists to not make.
 *
 * A usage whose type resolves to nothing keeps its own page, because there is
 * nowhere else to send it.
 *
 * @param detail one part, as `GET /api/parts/{id}` answers it
 */
export function resolveToDefinition(detail: PartDetail): Landing {
  if (detail.is_definition) return { id: detail.id, usage: null, unresolved: false }

  const usage: UsageContext = {
    id: detail.id,
    name: detail.name || detail.id,
    qualified_name: detail.qualified_name,
    multiplicity: String(detail.multiplicity ?? ''),
    owner: detail.owner ?? '',
    typeName: String(detail.type_name ?? ''),
  }

  if (!detail.definition) return { id: detail.id, usage, unresolved: true }
  return { id: detail.definition, usage, unresolved: false }
}
